import { CircleAlert, Info, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';
import { Button } from './button';

export type InlineAlertTone = 'error' | 'warning' | 'info';

const TONES: Record<InlineAlertTone, { box: string; icon: string; Icon: LucideIcon }> = {
    error: {
        box: 'border-red-200 bg-red-50 text-red-800',
        icon: 'text-red-600',
        Icon: CircleAlert,
    },
    warning: {
        box: 'border-amber-200 bg-amber-50 text-amber-900',
        icon: 'text-amber-600',
        Icon: TriangleAlert,
    },
    info: {
        box: 'border-primary-100 bg-primary-50 text-primary-700',
        icon: 'text-primary-500',
        Icon: Info,
    },
};

/** Inline status message: icon shape + optional title carry the tone; color reinforces it. */
export function InlineAlert({
    tone,
    title,
    children,
    className,
    onRetry,
    isRetrying = false,
}: {
    tone: InlineAlertTone;
    title?: ReactNode;
    children?: ReactNode;
    className?: string;
    onRetry?: () => void;
    isRetrying?: boolean;
}) {
    const { box, icon, Icon } = TONES[tone];
    return (
        <div
            role={tone === 'error' ? 'alert' : 'status'}
            className={cn('flex gap-3 rounded-lg border p-4 text-left text-sm', box, className)}
        >
            <Icon aria-hidden className={cn('mt-0.5 size-5 shrink-0', icon)} />
            {title || children || onRetry ? (
                <div className="min-w-0 flex-1 break-words">
                    {title ? <p className="font-semibold">{title}</p> : null}
                    {children ? <div className={title ? 'mt-1' : undefined}>{children}</div> : null}
                    {onRetry ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-3 bg-white"
                            onClick={onRetry}
                            disabled={isRetrying}
                        >
                            {isRetrying ? 'Trying again…' : 'Try again'}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
